import CustomText from '@components/shared/CustomText';
import ModalComponent from '@components/shared/ModalComponent';
import TablePagination from '@features/DocViewer/TablePagination';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { SIGNATURES_PAGE_LIMIT } from '@shared/constants';
import { useEffect, useState } from 'react';
import DocsMobile from './DocsMobile';
import DocsTable from './DocsTable';
import DocsTableHeader from './DocsTableHeader';

import useDocsTableFeatures from './useDocsTableFeatures';

import styles from './Docs.module.scss';

const Docs = ({
  data,
  hasPrevious,
  hasNext,
  fetchPage,
  changeSort,
  sort,
  phone,
  isLoading,
  count,
}) => {
  const {
    downloadSelected,
    handleRowSelect,
    handleSelectAllClick,
    onDownloadAll,
    selected,
    isDownloading,
    areAllSelected,
    setSelected,
  } = useDocsTableFeatures(data, phone, changeSort);

  const [modalOpen, setModalOpen] = useState(false);

  const onChangeSort = () => {
    setSelected({});
    changeSort();
  };

  useEffect(() => {
    if (!isDownloading) {
      setSelected({});
      setModalOpen(false);
    }
  }, [isDownloading, setSelected]);

  return (
    <div className={styles.container}>
      <CustomText customStyle={clsx('h2', styles.title)}>Doc Viewer</CustomText>

      <div className={styles.tableContainer}>
        <DocsTableHeader
          selected={selected}
          setModalOpen={setModalOpen}
          isDownloading={isDownloading}
          count={count}
          downloadSelected={downloadSelected}
          onChangeSort={onChangeSort}
          sort={sort}
          isLoading={isLoading}
        />
        <div className='hideOnMobile'>
          <DocsTable
            data={data}
            sort={sort}
            selected={selected}
            handleRowSelect={handleRowSelect}
            handleSelectAllClick={handleSelectAllClick}
            areAllSelected={areAllSelected}
            onChangeSort={onChangeSort}
          />
        </div>
        <div className='hideOnDesktop'>
          <DocsMobile
            data={data}
            onSelect={handleRowSelect}
            selected={selected}
            handleSelectAllClick={handleSelectAllClick}
            areAllSelected={areAllSelected}
          />
        </div>
        {(count > 0 || isLoading) && (
          <TablePagination
            pages={Math.ceil(data?.total / SIGNATURES_PAGE_LIMIT)}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            fetchPage={fetchPage}
          />
        )}
        {data !== undefined && !data?.signatures?.length ? (
          <div className={styles.noItems}>
            <CustomText customStyle={'body1'}>
              No signatures with provided number
            </CustomText>
          </div>
        ) : (
          ''
        )}
      </div>
      <ModalComponent
        isOpen={modalOpen}
        disabledPrimary={isDownloading}
        closeModal={() => setModalOpen(false)}
        primaryAction={() => onDownloadAll({ limit: 0, phone })}
        secondaryAction={() => setModalOpen(false)}
        primaryBtnText={isDownloading ? 'Downloading' : 'Download'}
        secondaryBtnText='Cancel'
        mainText='Are you sure you want to download all of your completed Docs?'
        secondaryText='This might take a while.'
        title={'Download all'}
      />
    </div>
  );
};

Docs.propTypes = {
  data: PropTypes.shape({
    signatures: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.string,
        date: PropTypes.string,
        form_name: PropTypes.string,
        signature_type: PropTypes.string,
        signed_doc_url: PropTypes.string,
      }),
    ),
    count: PropTypes.number,
    total: PropTypes.number,
  }),
  hasPrevious: PropTypes.bool,
  hasNext: PropTypes.bool,
  fetchPage: PropTypes.func,
  changeSort: PropTypes.func,
  sort: PropTypes.string,
  phone: PropTypes.string,
  isLoading: PropTypes.bool,
  count: PropTypes.number,
};

export default Docs;
